(() => {
    'use strict';

    const RUNTIME_ATTRIBUTE = 'data-recently-viewed-runtime-ready';
    const STORAGE_KEY = 'theme:recently-viewed';
    const MAX_STORED = 12;

    if (document.documentElement.hasAttribute(RUNTIME_ATTRIBUTE)) {
        return;
    }

    document.documentElement.setAttribute(RUNTIME_ATTRIBUTE, '');

    const sectionInstances = new WeakMap();

    class RecentlyViewedHistory {
        static normalize(value) {
            if (!Array.isArray(value)) {
                return [];
            }

            const seen = new Set();
            const normalized = [];

            value.forEach((item) => {
                if (typeof item !== 'string') {
                    return;
                }

                const handle = item.trim();

                if (
                    !handle ||
                    handle.length > 255 ||
                    /[\/\\?#\s]/.test(handle) ||
                    seen.has(handle)
                ) {
                    return;
                }

                seen.add(handle);
                normalized.push(handle);
            });

            return normalized.slice(0, MAX_STORED);
        }

        static read() {
            try {
                const currentValue = localStorage.getItem(STORAGE_KEY);

                if (currentValue === null) {
                    return [];
                }

                return this.normalize(
                    JSON.parse(currentValue)
                );
            } catch (_) {
                return [];
            }
        }

        static write(handles) {
            const normalized = this.normalize(handles);

            try {
                localStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify(normalized)
                );
            } catch (_) {
                // Storage may be unavailable in restricted environments.
            }

            return normalized;
        }

        static record(handle) {
            const normalized = this.normalize([handle]);

            if (!normalized.length) {
                return;
            }

            const currentHandle = normalized[0];

            const stored = this.read().filter(
                (existingHandle) => existingHandle !== currentHandle
            );

            stored.unshift(currentHandle);

            this.write(stored);
        }

        static get(excludeHandle) {
            const stored = this.read();

            if (!excludeHandle) {
                return stored;
            }

            return stored.filter(
                (handle) => handle !== excludeHandle
            );
        }

        static remove(handles) {
            const toRemove = new Set(
                this.normalize(handles)
            );

            if (!toRemove.size) {
                return;
            }

            this.write(
                this.read().filter(
                    (handle) => !toRemove.has(handle)
                )
            );
        }
    }

    class RecentlyViewedSection {
        constructor(container) {
            this.container = container;

            this.sectionId =
                container.dataset.sectionId || '';

            this.exclude =
                container.dataset.exclude || null;

            this.maxToShow =
                Number.parseInt(
                    container.dataset.productsToShow,
                    10
                ) || 6;

            this.designMode =
                container.dataset.designMode === 'true';

            this.content =
                container.querySelector(
                    '[data-recently-viewed-content]'
                );

            this.track =
                container.querySelector(
                    '[data-recently-viewed-track]'
                );

            this.navigation =
                container.querySelector(
                    '[data-recently-viewed-navigation]'
                );

            this.previousButton =
                container.querySelector(
                    '[data-recently-viewed-previous]'
                );

            this.nextButton =
                container.querySelector(
                    '[data-recently-viewed-next]'
                );

            this.cards = [];

            this.isLoading = false;

            this.scrollFrame = null;

            this.intersectionObserver = null;

            this.resizeObserver = null;

            this.abortController =
                new AbortController();

            this.init();
        }

        init() {
            if (
                !this.sectionId ||
                !this.content ||
                !this.track
            ) {
                return;
            }

            if (
                this.designMode ||
                !('IntersectionObserver' in window)
            ) {
                this.load();
                return;
            }

            this.intersectionObserver =
                new IntersectionObserver(
                    (entries, observer) => {
                        entries.forEach((entry) => {
                            if (!entry.isIntersecting) {
                                return;
                            }

                            observer.unobserve(
                                entry.target
                            );

                            this.load();
                        });
                    },
                    {
                        rootMargin: '500px 0px',
                    }
                );

            this.intersectionObserver.observe(
                this.container
            );
        }

        async load() {
            if (this.isLoading) {
                return;
            }

            this.isLoading = true;

            this.container.setAttribute(
                'aria-busy',
                'true'
            );

            const handles =
                RecentlyViewedHistory
                    .get(this.exclude)
                    .slice(
                        0,
                        this.maxToShow
                    );

            if (!handles.length) {
                this.handleEmpty();
                return;
            }

            let results;

            try {
                results =
                    await this.fetchCards(
                        handles,
                        4
                    );
            } catch (_) {
                if (
                    this.abortController
                        .signal.aborted
                ) {
                    return;
                }

                this.handleEmpty();
                return;
            }

            if (
                this.abortController
                    .signal.aborted ||
                !this.container.isConnected
            ) {
                return;
            }

            const staleHandles =
                results
                    .filter(
                        (result) =>
                            result &&
                            result.status === 404
                    )
                    .map(
                        (result) =>
                            result.handle
                    );

            if (staleHandles.length) {
                RecentlyViewedHistory.remove(
                    staleHandles
                );
            }

            const validResults =
                results.filter(
                    (result) =>
                        result &&
                        result.fragment
                );

            if (!validResults.length) {
                this.handleEmpty();
                return;
            }

            const fragment =
                document.createDocumentFragment();

            validResults.forEach(
                (result) => {
                    fragment.append(
                        result.fragment
                    );
                }
            );

            this.track.replaceChildren(
                fragment
            );

            this.content.hidden = false;

            this.container.classList.remove(
                'recently-viewed--pending'
            );

            this.container.classList.add(
                'recently-viewed--ready'
            );

            this.container.setAttribute(
                'aria-busy',
                'false'
            );

            this.isLoading = false;

            this.setupRail();

            document.dispatchEvent(
                new CustomEvent(
                    'theme:recently-viewed:loaded',
                    {
                        bubbles: true,

                        detail: {
                            sectionId:
                                this.sectionId,

                            count:
                                this.cards.length,
                        },
                    }
                )
            );
        }

        handleEmpty() {
            this.isLoading = false;

            this.container.setAttribute(
                'aria-busy',
                'false'
            );

            if (this.designMode) {
                this.content.hidden = false;
                return;
            }

            this.destroy();

            this.container.remove();
        }

        async fetchCards(
            handles,
            concurrency
        ) {
            const results =
                new Array(
                    handles.length
                );

            let nextIndex = 0;

            const worker = async () => {
                while (
                    nextIndex < handles.length &&
                    !this.abortController
                        .signal.aborted
                ) {
                    const index = nextIndex;

                    nextIndex += 1;

                    results[index] =
                        await this.fetchCard(
                            handles[index]
                        );
                }
            };

            const workerCount =
                Math.min(
                    concurrency,
                    handles.length
                );

            await Promise.all(
                Array.from(
                    {
                        length: workerCount,
                    },
                    () => worker()
                )
            );

            return results;
        }

        async fetchCard(handle) {
            const shopRoot =
                window.Shopify
                    ?.routes
                    ?.root || '/';

            const normalizedRoot =
                shopRoot.endsWith('/')
                    ? shopRoot
                    : `${shopRoot}/`;

            const productUrl =
                `${normalizedRoot}products/${encodeURIComponent(
                    handle
                )}?section_id=${encodeURIComponent(
                    this.sectionId
                )}`;

            try {
                const response =
                    await fetch(
                        productUrl,
                        {
                            credentials:
                                'same-origin',

                            headers: {
                                Accept:
                                    'text/html',
                            },

                            signal:
                                this.abortController
                                    .signal,
                        }
                    );

                if (!response.ok) {
                    return {
                        handle,
                        fragment: null,
                        status:
                            response.status,
                    };
                }

                const html =
                    await response.text();

                const parsedDocument =
                    new DOMParser()
                        .parseFromString(
                            html,
                            'text/html'
                        );

                const template =
                    parsedDocument.querySelector(
                        'template[data-recently-viewed-card]'
                    );

                if (!template) {
                    return {
                        handle,
                        fragment: null,
                        status: 200,
                    };
                }

                const fragment =
                    document.importNode(
                        template.content,
                        true
                    );

                if (
                    !fragment.childElementCount
                ) {
                    return {
                        handle,
                        fragment: null,
                        status: 200,
                    };
                }

                return {
                    handle,
                    fragment,
                    status: 200,
                };
            } catch (error) {
                if (
                    error?.name ===
                    'AbortError'
                ) {
                    return null;
                }

                return {
                    handle,
                    fragment: null,
                    status: 0,
                };
            }
        }

        setupRail() {
            this.cards =
                Array.from(
                    this.track.children
                );

            if (!this.cards.length) {
                return;
            }

            const signal =
                this.abortController
                    .signal;

            this.track.addEventListener(
                'scroll',
                () => {
                    if (this.scrollFrame) {
                        cancelAnimationFrame(
                            this.scrollFrame
                        );
                    }

                    this.scrollFrame =
                        requestAnimationFrame(
                            () => {
                                this.scrollFrame =
                                    null;

                                this.updateRailState();
                            }
                        );
                },
                {
                    passive: true,
                    signal,
                }
            );

            this.previousButton
                ?.addEventListener(
                    'click',
                    () => this.move(-1),
                    {
                        signal,
                    }
                );

            this.nextButton
                ?.addEventListener(
                    'click',
                    () => this.move(1),
                    {
                        signal,
                    }
                );

            if (
                'ResizeObserver'
                in window
            ) {
                this.resizeObserver =
                    new ResizeObserver(
                        () =>
                            this.updateRailState()
                    );

                this.resizeObserver.observe(
                    this.track
                );
            } else {
                window.addEventListener(
                    'resize',
                    () =>
                        this.updateRailState(),
                    {
                        signal,
                    }
                );
            }

            requestAnimationFrame(
                () =>
                    this.updateRailState()
            );
        }

        getClosestIndex() {
            if (!this.cards.length) {
                return 0;
            }

            const trackRect =
                this.track
                    .getBoundingClientRect();

            const rtl =
                getComputedStyle(
                    this.track
                ).direction === 'rtl';

            let closestIndex = 0;

            let closestDistance =
                Infinity;

            this.cards.forEach(
                (
                    card,
                    index
                ) => {
                    const rect =
                        card
                            .getBoundingClientRect();

                    const distance =
                        rtl
                            ? Math.abs(
                                trackRect.right -
                                rect.right
                            )
                            : Math.abs(
                                rect.left -
                                trackRect.left
                            );

                    if (
                        distance <
                        closestDistance
                    ) {
                        closestDistance =
                            distance;

                        closestIndex =
                            index;
                    }
                }
            );

            return closestIndex;
        }

        getVisibleCount() {
            const firstCard =
                this.cards[0];

            if (!firstCard) {
                return 1;
            }

            const styles =
                getComputedStyle(
                    this.track
                );

            const gap =
                Number.parseFloat(
                    styles.columnGap
                ) || 0;

            const cardWidth =
                firstCard
                    .getBoundingClientRect()
                    .width;

            if (!cardWidth) {
                return 1;
            }

            return Math.max(
                1,
                Math.floor(
                    (
                        this.track.clientWidth +
                        gap
                    ) /
                    (
                        cardWidth +
                        gap
                    )
                )
            );
        }

        updateRailState() {
            if (!this.cards.length) {
                return;
            }

            const canScroll =
                this.track.scrollWidth -
                this.track.clientWidth >
                2;

            if (this.navigation) {
                this.navigation.hidden =
                    !canScroll;
            }

            if (!canScroll) {
                if (
                    this.previousButton
                ) {
                    this.previousButton.disabled =
                        true;
                }

                if (
                    this.nextButton
                ) {
                    this.nextButton.disabled =
                        true;
                }

                return;
            }

            const currentIndex =
                this.getClosestIndex();

            const visibleCount =
                this.getVisibleCount();

            const maxStartIndex =
                Math.max(
                    0,
                    this.cards.length -
                    visibleCount
                );

            if (
                this.previousButton
            ) {
                this.previousButton.disabled =
                    currentIndex <= 0;
            }

            if (
                this.nextButton
            ) {
                this.nextButton.disabled =
                    currentIndex >=
                    maxStartIndex;
            }
        }

        move(direction) {
            if (!this.cards.length) {
                return;
            }

            const currentIndex =
                this.getClosestIndex();

            const visibleCount =
                this.getVisibleCount();

            const maxStartIndex =
                Math.max(
                    0,
                    this.cards.length -
                    visibleCount
                );

            const targetIndex =
                Math.max(
                    0,
                    Math.min(
                        currentIndex +
                        direction,
                        maxStartIndex
                    )
                );

            const target =
                this.cards[
                targetIndex
                ];

            if (!target) {
                return;
            }

            const reducedMotion =
                window.matchMedia(
                    '(prefers-reduced-motion: reduce)'
                ).matches;

            target.scrollIntoView({
                behavior:
                    reducedMotion
                        ? 'auto'
                        : 'smooth',

                block:
                    'nearest',

                inline:
                    'start',
            });
        }

        destroy() {
            this.abortController.abort();

            this.intersectionObserver
                ?.disconnect();

            this.resizeObserver
                ?.disconnect();

            if (
                this.scrollFrame
            ) {
                cancelAnimationFrame(
                    this.scrollFrame
                );
            }
        }
    }

    const getSections = (
        root = document
    ) => {
        const sections = [];

        if (
            root instanceof Element &&
            root.matches(
                '[data-recently-viewed]'
            )
        ) {
            sections.push(root);
        }

        sections.push(
            ...(
                root.querySelectorAll?.(
                    '[data-recently-viewed]'
                ) || []
            )
        );

        return sections;
    };

    const initialize = (
        root = document
    ) => {
        getSections(root)
            .forEach(
                (container) => {
                    if (
                        sectionInstances.has(
                            container
                        )
                    ) {
                        return;
                    }

                    const instance =
                        new RecentlyViewedSection(
                            container
                        );

                    sectionInstances.set(
                        container,
                        instance
                    );
                }
            );
    };

    const destroy = (
        root
    ) => {
        getSections(root)
            .forEach(
                (container) => {
                    const instance =
                        sectionInstances.get(
                            container
                        );

                    instance?.destroy();

                    sectionInstances.delete(
                        container
                    );
                }
            );
    };

    const recordCurrentProduct =
        () => {
            const handle =
                document.body
                    ?.dataset
                    ?.productHandle;

            if (!handle) {
                return;
            }

            RecentlyViewedHistory.record(
                handle
            );
        };

    const boot = () => {
        recordCurrentProduct();

        initialize(document);
    };

    if (
        document.readyState ===
        'loading'
    ) {
        document.addEventListener(
            'DOMContentLoaded',
            boot,
            {
                once: true,
            }
        );
    } else {
        boot();
    }

    document.addEventListener(
        'shopify:section:load',
        (event) => {
            initialize(
                event.target
            );
        }
    );

    document.addEventListener(
        'shopify:section:unload',
        (event) => {
            destroy(
                event.target
            );
        }
    );
})();